/**
 * CommunityMvvSection — Mission, Vision, Values display + inline edit.
 *
 * Renders three cards (Mission, Vision, Values) on the Alignment tab.
 * Supports multi-language display via three-tier fallback.
 * Edit modal allows community admins to author content and add translations.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import {
  CommunityMvv,
  ValueItem,
  MvvTranslations,
  fetchCommunityMvv,
  upsertCommunityMvv,
  resolveField,
  resolveValues,
} from '../../api/communityMvv';
import i18n from '../../i18n/config';
import styles from './CommunityMvvSection.module.css';

const SUPPORTED_LANGS = ['en', 'ar', 'de', 'es', 'fr', 'ru', 'sv', 'uk'] as const;
const LANG_LABELS: Record<string, string> = {
  en: 'English', ar: 'العربية', de: 'Deutsch', es: 'Español',
  fr: 'Français', ru: 'Русский', sv: 'Svenska', uk: 'Українська',
};

/** Number of values visible before truncation */
const VALUES_VISIBLE_COUNT = 3;

interface Props {
  communityGuid: string;
  canEdit: boolean;
}

export default function CommunityMvvSection({ communityGuid, canEdit }: Props) {
  const { t } = useAppTranslation('pages');
  const currentLang = i18n.language?.substring(0, 2) || 'en';

  const [mvv, setMvv] = useState<CommunityMvv | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [valuesExpanded, setValuesExpanded] = useState(false);

  // Fetch MVV data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCommunityMvv(communityGuid)
      .then((data) => { if (!cancelled) setMvv(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [communityGuid]);

  const handleSaved = useCallback((updated: CommunityMvv) => {
    setMvv(updated);
    setEditOpen(false);
  }, []);

  if (loading) return null;

  // Resolve display values for the current language
  const mission = mvv ? resolveField(mvv, 'mission', currentLang) : '';
  const vision = mvv ? resolveField(mvv, 'vision', currentLang) : '';
  const values = mvv ? resolveValues(mvv, currentLang) : [];

  // Don't show the section if completely empty and user can't edit
  const isEmpty = !mission && !vision && values.length === 0;
  if (isEmpty && !canEdit) return null;

  const hiddenCount = values.length - VALUES_VISIBLE_COUNT;
  const displayedValues = valuesExpanded ? values : values.slice(0, VALUES_VISIBLE_COUNT);

  return (
    <div className={styles.section}>
      <div className={styles.band}>
        {canEdit && (
          <button type="button" className={styles.bandMenuBtn} onClick={() => setEditOpen(true)}
            title={t('communityDetail.mvvEdit')}>⋯</button>
        )}
        <div className={styles.bandInner}>
          {/* Mission */}
          <div className={styles.column}>
            <p className={styles.cardLabel}>{t('communityDetail.mission')}</p>
            {mission ? (
              <p className={styles.cardText}>{mission}</p>
            ) : (
              <p className={styles.emptyText}>{t('communityDetail.mvvEmpty')}</p>
            )}
          </div>

          <div className={styles.divider} />

          {/* Vision */}
          <div className={styles.column}>
            <p className={styles.cardLabel}>{t('communityDetail.vision')}</p>
            {vision ? (
              <p className={styles.cardText}>{vision}</p>
            ) : (
              <p className={styles.emptyText}>{t('communityDetail.mvvEmpty')}</p>
            )}
          </div>

          <div className={styles.divider} />

          {/* Values */}
          <div className={styles.column}>
            <p className={styles.cardLabel}>{t('communityDetail.values')}</p>
          {values.length > 0 ? (
            <>
              <ul className={styles.valuesList}>
                {displayedValues.map((v, i) => (
                  <li key={i} className={styles.valueItem}>
                    <span className={styles.valueTitle}>{v.title}</span>
                    {v.description && <span className={styles.valueDesc}>{v.description}</span>}
                  </li>
                ))}
              </ul>
              {hiddenCount > 0 && !valuesExpanded && (
                <button
                  type="button"
                  className={styles.valuesToggle}
                  onClick={() => setValuesExpanded(true)}
                >
                  + {hiddenCount} {t('communityDetail.moreValues')}
                </button>
              )}
              {valuesExpanded && values.length > VALUES_VISIBLE_COUNT && (
                <button
                  type="button"
                  className={styles.valuesToggle}
                  onClick={() => setValuesExpanded(false)}
                >
                  {t('communityDetail.showLess')}
                </button>
              )}
            </>
          ) : (
            <p className={styles.emptyText}>{t('communityDetail.mvvEmpty')}</p>
          )}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editOpen && createPortal(
        <MvvEditModal
          communityGuid={communityGuid}
          mvv={mvv}
          onClose={() => setEditOpen(false)}
          onSaved={handleSaved}
        />,
        document.body,
      )}
    </div>
  );
}

// ── Edit Modal ─────────────────────────────────────────────────

interface EditModalProps {
  communityGuid: string;
  mvv: CommunityMvv | null;
  onClose: () => void;
  onSaved: (updated: CommunityMvv) => void;
}

function MvvEditModal({ communityGuid, mvv, onClose, onSaved }: EditModalProps) {
  const { t } = useAppTranslation('pages');

  // Editing language — 'en' means editing the canonical fields
  const [editLang, setEditLang] = useState('en');

  // Canonical (English) fields
  const [mission, setMission] = useState(mvv?.mission ?? '');
  const [vision, setVision] = useState(mvv?.vision ?? '');
  const [values, setValues] = useState<ValueItem[]>(mvv?.values ?? []);

  // Translations
  const [translations, setTranslations] = useState<MvvTranslations>(mvv?.translations ?? {});

  const [saving, setSaving] = useState(false);

  // Stable key counter for value rows — avoids index-based key issues
  const nextKeyRef = useRef(Math.max(values.length, 1));

  // Each value row gets a stable key; seed from initial length
  const [valueKeys, setValueKeys] = useState<number[]>(() =>
    values.map((_, i) => i)
  );

  // Get current field value for the selected language
  const getMission = () => editLang === 'en' ? mission : (translations.mission?.[editLang] ?? '');
  const getVision = () => editLang === 'en' ? vision : (translations.vision?.[editLang] ?? '');
  const getValues = (): ValueItem[] => editLang === 'en' ? values : (translations.values?.[editLang] ?? []);

  // Set field value for the selected language
  const setMissionForLang = (v: string) => {
    if (editLang === 'en') { setMission(v); return; }
    setTranslations((prev) => ({
      ...prev,
      mission: { ...prev.mission, [editLang]: v },
    }));
  };

  const setVisionForLang = (v: string) => {
    if (editLang === 'en') { setVision(v); return; }
    setTranslations((prev) => ({
      ...prev,
      vision: { ...prev.vision, [editLang]: v },
    }));
  };

  const setValuesForLang = (updated: ValueItem[]) => {
    if (editLang === 'en') { setValues(updated); return; }
    setTranslations((prev) => ({
      ...prev,
      values: { ...prev.values, [editLang]: updated },
    }));
  };

  const addValue = () => {
    setValuesForLang([...getValues(), { title: '', description: '' }]);
    const key = nextKeyRef.current++;
    setValueKeys((prev) => [...prev, key]);
  };

  const updateValue = (idx: number, field: keyof ValueItem, v: string) => {
    const updated = [...getValues()];
    updated[idx] = { ...updated[idx], [field]: v };
    setValuesForLang(updated);
  };

  const removeValue = (idx: number) => {
    setValuesForLang(getValues().filter((_, i) => i !== idx));
    setValueKeys((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await upsertCommunityMvv(communityGuid, {
        mission,
        vision,
        values,
        translations,
      });
      onSaved(result);
    } catch (err) {
      console.error('Failed to save MVV:', err);
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const currentValues = getValues();

  return (
    <div
      className={styles.modalBackdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('communityDetail.mvvEditTitle')}</h2>
          <CloseButton onClick={onClose} size="md" />
        </div>

        {/* Language selector */}
        <div className={styles.langSelector}>
          <span className={styles.langLabel}>{t('communityDetail.mvvLanguage')}:</span>
          <select
            className={styles.langSelect}
            value={editLang}
            onChange={(e) => setEditLang(e.target.value)}
          >
            {SUPPORTED_LANGS.map((lang) => (
              <option key={lang} value={lang}>
                {LANG_LABELS[lang]} {lang === 'en' ? `(${t('communityDetail.mvvCanonical')})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Mission */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('communityDetail.mission')}</label>
          <textarea
            className={styles.textarea}
            value={getMission()}
            onChange={(e) => setMissionForLang(e.target.value)}
            placeholder={t('communityDetail.mvvMissionPlaceholder')}
          />
        </div>

        {/* Vision */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('communityDetail.vision')}</label>
          <textarea
            className={styles.textarea}
            value={getVision()}
            onChange={(e) => setVisionForLang(e.target.value)}
            placeholder={t('communityDetail.mvvVisionPlaceholder')}
          />
        </div>

        {/* Values */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('communityDetail.values')}</label>
          <div className={styles.valuesEditor}>
            {currentValues.map((v, idx) => (
              <div key={valueKeys[idx] ?? idx} className={styles.valueRow}>
                <div className={styles.valueInputs}>
                  <input
                    className={styles.valueInput}
                    value={v.title}
                    onChange={(e) => updateValue(idx, 'title', e.target.value)}
                    placeholder={t('communityDetail.mvvValueTitle')}
                  />
                  <input
                    className={styles.valueInput}
                    value={v.description}
                    onChange={(e) => updateValue(idx, 'description', e.target.value)}
                    placeholder={t('communityDetail.mvvValueDesc')}
                  />
                </div>
                <button type="button" className={styles.removeValueBtn} onClick={() => removeValue(idx)}>×</button>
              </div>
            ))}
            <button type="button" className={styles.addValueBtn} onClick={addValue}>
              + {t('communityDetail.mvvAddValue')}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            {t('communityDetail.mvvCancel')}
          </button>
          <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? t('communityDetail.mvvSaving') : t('communityDetail.mvvSave')}
          </button>
        </div>
      </div>
    </div>
  );
}
